import React from 'react';
import { formatCurrencyValue } from 'ts/common/components/invoice/helpers';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import { sanitizeNumber } from 'client_react/booking/helpers';
import { ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import ClientPaymentSummary from 'client_react/common/components/ClientPaymentSummary';

interface Props {
    paymentAmountType: ClientPaymentOption;
    showTipSection?: boolean;
    tipAmount?: number;
}

const PaymentSummary = ({ paymentAmountType, showTipSection, tipAmount }: Props) => {
    const { bookingSession } = useClientBookingExperience();
    const t = useTranslate();

    if (!bookingSession) {
        return null;
    }

    const isFullPayment = paymentAmountType === 'full';

    const subtotal = isFullPayment ? bookingSession.price : bookingSession.retainerPaymentAmount;

    const salesTax = isFullPayment
        ? bookingSession.salesTaxForPrice
        : bookingSession.salesTaxForRetainerPaymentAmount;

    const totalPrice = formatCurrencyValue(
        sanitizeNumber(
            isFullPayment
                ? bookingSession.priceWithSalesTax
                : bookingSession.retainerPaymentAmountWithSalesTax
        ) + (tipAmount ?? 0),
        bookingSession.currencyCode
    );

    return (
        <ClientPaymentSummary
            showBreakdown={bookingSession.isTaxable}
            salesTax={salesTax}
            salesTaxSubtitle={
                bookingSession.salesTaxTitle || t('booking.payment.paymentSummaryTax')
            }
            subtotal={subtotal}
            subtotalSubtitle={t('booking.payment.paymentSummarySubtotal')}
            tipAmount={
                showTipSection
                    ? formatCurrencyValue(tipAmount ?? 0, bookingSession.currencyCode)
                    : undefined
            }
            tipAmountSubtitle={showTipSection ? t('tip') : undefined}
            title={t('booking.payment.paymentSummary')}
            totalPrice={totalPrice}
            totalSubtitle={t('booking.payment.paymentSummaryTotal')}
        />
    );
};

export default PaymentSummary;
